/**
 * Color helpers for the DXF writer: hex ↔ true-color int + a conservative
 * hex → ACI approximation used below AC1018 (where group 420 is illegal)
 * and for group 62, which every reader resolves.
 */

/** '#rrggbb' → 0xRRGGBB int (null when the value is not a hex color). */
export function hexToInt(hex: unknown): number | null {
    if (typeof hex !== 'string' || !hex.startsWith('#')) return null;
    const h = hex.slice(1);
    if (h.length < 6) return null;
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    if ([r, g, b].some((v) => !Number.isFinite(v))) return null;
    return (r << 16) | (g << 8) | b;
}

/**
 * Approximate an ACI index (62) from a hex color: the classic primaries +
 * grayscale steps, falling back to 256 (ByLayer) for anything unresolvable.
 * AutoCAD renders 256 as the layer color, which is the honest mapping for
 * arbitrary palette colors under R14/2000.
 */
export function hexToAci(hex: unknown): number {
    const rgb = hexToInt(hex);
    if (rgb === null) return 256;
    const r = (rgb >> 16) & 0xff, g = (rgb >> 8) & 0xff, b = rgb & 0xff;
    if (r > 200 && g < 60 && b < 60) return 1;
    if (r > 200 && g > 200 && b < 60) return 2;
    if (r < 60 && g > 150 && b < 60) return 3;
    if (r < 60 && g < 60 && b > 200) return 5;
    if (r > 200 && g < 60 && b > 200) return 6;
    if (r < 60 && g > 200 && b > 200) return 4;
    if (r > 200 && g > 200 && b > 200) return 7;
    if (r < 30 && g < 30 && b < 30) return 7;
    const gray = (r + g + b) / 3;
    if (Math.abs(r - gray) < 12 && Math.abs(g - gray) < 12 && Math.abs(b - gray) < 12) {
        if (gray > 60 && gray < 200) return 8; // dark gray band
        if (gray >= 200) return 7; // light gray → white
    }
    return 256;
}

/** '#' + 6-digit hex for an ACI 1-7 (used by nothing else in Faz 1). */
export function aciToHex(aci: number): string {
    const table: Record<number, string> = {
        1: '#ff0000', 2: '#ffff00', 3: '#00ff00', 4: '#00ffff', 5: '#0000ff', 6: '#ff00ff', 7: '#ffffff',
    };
    return table[aci] ?? '#ffffff';
}
